"use client";

// Voice-announcement preferences modal.
//
// Reads + writes localStorage via lib/voice/preferences.ts; dispatches
// the `rust-sight:voice-prefs-changed` window event after every write
// so useVoiceAnnouncer (running in MapView) re-applies prefs without
// a refresh. The modal itself doesn't speak — it asks
// useSpeechSynthesis to do a single "Voice announcements enabled"
// utterance when the user clicks Test voice, which also doubles as
// the autoplay-policy unlock gesture.

import { useEffect, useMemo, useState } from "react";
import { useSpeechSynthesis, pickDefaultVoice } from "@/hooks/useSpeechSynthesis";
import {
  patchVoicePrefs,
  readVoicePrefs,
  setRulePref,
  type VoicePreferences,
} from "@/lib/voice/preferences";
import {
  DAY_NIGHT_DEFAULT_THRESHOLD_MIN,
  DAY_NIGHT_RULE_ID,
} from "@/lib/voice/rules/day-night";
import { VOICE_PREFS_CHANGED_EVENT } from "@/hooks/useVoiceAnnouncer";

function emitPrefsChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(VOICE_PREFS_CHANGED_EVENT));
}

export function VoiceSettings({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { supported, voices, speak, unlocked } = useSpeechSynthesis();
  const [prefs, setPrefs] = useState<VoicePreferences>(readVoicePrefs());

  useEffect(() => {
    if (open) setPrefs(readVoicePrefs());
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const sortedVoices = useMemo(() => {
    return [...voices].sort((a, b) => a.name.localeCompare(b.name));
  }, [voices]);

  // What voice the picker shows as "selected" — either the saved
  // voiceURI or our suggested default.
  const effectiveVoiceURI =
    prefs.voiceURI ?? pickDefaultVoice(voices)?.voiceURI ?? "";

  const dayNightPref = prefs.rules[DAY_NIGHT_RULE_ID];
  const dayNightEnabled = dayNightPref?.enabled ?? true;
  const dayNightThreshold = Number(
    (dayNightPref?.params as { thresholdMinutes?: unknown } | undefined)?.thresholdMinutes
    ?? DAY_NIGHT_DEFAULT_THRESHOLD_MIN,
  );

  function update<K extends keyof VoicePreferences>(key: K, value: VoicePreferences[K]) {
    const next = patchVoicePrefs({ [key]: value } as Partial<VoicePreferences>);
    setPrefs(next);
    emitPrefsChanged();
  }

  function updateDayNightThreshold(minutes: number) {
    const next = setRulePref(DAY_NIGHT_RULE_ID, {
      enabled: dayNightEnabled,
      params: { thresholdMinutes: minutes },
    });
    setPrefs(next);
    emitPrefsChanged();
  }

  function toggleDayNightEnabled(on: boolean) {
    const next = setRulePref(DAY_NIGHT_RULE_ID, {
      enabled: on,
      params: { thresholdMinutes: dayNightThreshold },
    });
    setPrefs(next);
    emitPrefsChanged();
  }

  function testVoice() {
    // Speaking also fires the autoplay-unlock gesture on browsers
    // that gate the API. We persist the unlocked state if it succeeds.
    speak("Voice announcements enabled.", {
      voiceURI: effectiveVoiceURI || null,
      volume: prefs.volume,
    });
    if (!prefs.audioUnlocked) update("audioUnlocked", true);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Voice announcement settings"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 480, width: "100%",
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: "1.25rem 1.4rem",
          color: "var(--fg)",
          display: "flex", flexDirection: "column", gap: "1rem",
          fontSize: "0.95rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem" }}>Voice announcements</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent", border: "none",
              color: "var(--fg-muted)", cursor: "pointer",
              fontSize: "1.4rem", lineHeight: 1, padding: "0 0.25rem",
            }}
          >×</button>
        </div>

        {!supported ? (
          <p style={{ color: "var(--fg-muted)", margin: 0 }}>
            This browser doesn&apos;t expose the Web Speech API. Voice
            announcements aren&apos;t available here.
          </p>
        ) : null}

        <label style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <input
            type="checkbox"
            checked={prefs.enabled}
            onChange={(e) => update("enabled", e.target.checked)}
            disabled={!supported}
          />
          <span>Enable voice announcements</span>
        </label>

        <div style={{ display: "grid", gap: "0.4rem" }}>
          <label htmlFor="voice-picker" style={{ color: "var(--fg-muted)", fontSize: "0.85rem" }}>
            Voice
          </label>
          <select
            id="voice-picker"
            value={effectiveVoiceURI}
            disabled={!supported || !prefs.enabled}
            onChange={(e) => update("voiceURI", e.target.value || null)}
            style={{
              padding: "0.4rem 0.5rem",
              background: "var(--bg)",
              color: "var(--fg)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              fontSize: "0.9rem",
            }}
          >
            <option value="">(Auto: best available)</option>
            {sortedVoices.map((v) => (
              <option key={v.voiceURI} value={v.voiceURI}>
                {v.name} — {v.lang}
              </option>
            ))}
          </select>
          <p style={{ color: "var(--fg-muted)", fontSize: "0.78rem", margin: "0.2rem 0 0" }}>
            On Windows, &quot;Microsoft Aria Online (Natural)&quot; is the closest match to a
            Cortana-style voice. Other systems fall back to whatever the OS provides.
          </p>
        </div>

        <div style={{ display: "grid", gap: "0.4rem" }}>
          <label htmlFor="vol" style={{ color: "var(--fg-muted)", fontSize: "0.85rem" }}>
            Volume — {Math.round(prefs.volume * 100)}%
          </label>
          <input
            id="vol"
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={prefs.volume}
            disabled={!supported || !prefs.enabled}
            onChange={(e) => update("volume", Number(e.target.value))}
          />
        </div>

        <button
          type="button"
          onClick={testVoice}
          disabled={!supported || !prefs.enabled}
          style={{
            alignSelf: "flex-start",
            padding: "0.45rem 0.95rem",
            border: "1px solid var(--accent)",
            background: "transparent",
            color: "var(--accent)",
            borderRadius: 6,
            cursor: supported && prefs.enabled ? "pointer" : "default",
            fontSize: "0.88rem",
          }}
        >
          Test voice
        </button>

        {supported && prefs.enabled && !unlocked ? (
          <p style={{ color: "#fcd34d", fontSize: "0.82rem", margin: 0 }}>
            This browser may need a click before audio can play. Use the
            Test voice button to unlock.
          </p>
        ) : null}

        <div
          style={{
            borderTop: "1px solid var(--border)",
            paddingTop: "0.9rem",
            display: "grid", gap: "0.55rem",
          }}
        >
          <div style={{ color: "var(--fg-muted)", fontSize: "0.85rem" }}>Announcements</div>

          <label style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <input
              type="checkbox"
              checked={dayNightEnabled}
              disabled={!supported || !prefs.enabled}
              onChange={(e) => toggleDayNightEnabled(e.target.checked)}
            />
            <span>Warn me before sunrise / sunset</span>
          </label>

          <label
            htmlFor="dn-threshold"
            style={{
              marginLeft: "1.6rem",
              color: "var(--fg-muted)",
              fontSize: "0.82rem",
              display: "flex", alignItems: "center", gap: "0.5rem",
            }}
          >
            <span>Warn this many minutes ahead:</span>
            <input
              id="dn-threshold"
              type="number"
              min={1}
              max={30}
              step={1}
              value={dayNightThreshold}
              disabled={!supported || !prefs.enabled || !dayNightEnabled}
              onChange={(e) => {
                const n = Math.max(1, Math.min(30, Math.round(Number(e.target.value))));
                if (Number.isFinite(n)) updateDayNightThreshold(n);
              }}
              style={{
                width: "4.5rem",
                padding: "0.25rem 0.4rem",
                background: "var(--bg)",
                color: "var(--fg)",
                border: "1px solid var(--border)",
                borderRadius: 4,
                fontSize: "0.85rem",
              }}
            />
          </label>
        </div>

        <p style={{ color: "var(--fg-muted)", fontSize: "0.78rem", margin: 0 }}>
          Preferences are saved on this device only.
        </p>
      </div>
    </div>
  );
}
